import { Camera, ChevronRight, Edit3, FileImage, History, Plus, ScanSearch, Share2, X } from 'lucide-react';
import { PersonAvatar, cx } from '../../components/ui';
import type { EventItem, MediaItem, Person, Role } from '../../types';

const generationNames = new Map([[15, '十五世'], [16, '十六世'], [17, '十七世'], [18, '十八世'], [19, '十九世']]);
const privacyNames = { public: '公开', family: '家族可见', private: '私密' };

export function PersonDetails({ person, people, media, events, role, mobileOpen, onCloseMobile, onEdit, onUpload, onShare, onOpenMedia }: { person: Person; people: Person[]; media: MediaItem[]; events: EventItem[]; role: Role; mobileOpen: boolean; onCloseMobile: () => void; onEdit: () => void; onUpload: () => void; onShare: () => void; onOpenMedia: (item: MediaItem) => void }) {
  const byId = new Map(people.map((item) => [item.id, item]));
  const parents = person.parentIds.map((id) => byId.get(id)?.name).filter(Boolean);
  const spouses = person.spouseIds.map((id) => byId.get(id)?.name).filter(Boolean);
  const children = person.childIds.map((id) => byId.get(id)?.name).filter(Boolean);
  const personMedia = media.filter((item) => item.personId === person.id);
  const personEvents = events.filter((item) => !item.personId || item.personId === person.id);

  return (
    <aside className={cx('person-details', mobileOpen && 'mobile-open')}>
      <button type="button" className="details-mobile-close" onClick={onCloseMobile} aria-label="关闭详情"><X /></button>
      <header className="person-summary">
        <div className="person-photo-wrap">
          <PersonAvatar person={person} large />
          <button type="button" className="photo-action" onClick={onUpload} aria-label="上传头像"><Camera /></button>
        </div>
        <div className="person-heading">
          <div><h2>{person.name}</h2><span>{generationNames.get(person.generation) ?? `${person.generation}世`}</span></div>
          <p>{person.birth}年 {person.death ? `- ${person.death}年` : '- 至今'}</p>
          <div className="person-tags"><span>{person.gender}</span><span>{person.origin || person.region}</span><span>{privacyNames[person.privacy]}</span></div>
        </div>
      </header>

      <div className="details-scroll">
        <section className="details-section relations-section">
          <div className="section-title"><h3>亲属关系</h3><span>{person.branch}</span></div>
          {[
            ['父母', parents.join('、') || '未录入'],
            ['配偶', spouses.join('、') || '未录入'],
            ['子女', children.join('、') || '未录入']
          ].map(([label, value]) => <div key={label} className="relation-row"><span>{label}</span><strong>{value}</strong><ChevronRight /></div>)}
        </section>

        <section className="details-section biography-section">
          <div className="section-title"><h3>生平简介</h3>{role !== 'guest' && <button type="button" onClick={onEdit}>编辑</button>}</div>
          <p>{person.privacy === 'private' && role === 'guest' ? '该成员信息仅家族内部可见。' : person.biography || '暂无生平简介。'}</p>
        </section>

        <section className="details-section media-section">
          <div className="section-title"><h3>影像档案</h3>{role !== 'guest' && <button type="button" onClick={onUpload}>上传</button>}</div>
          <div className="media-strip">
            {personMedia.slice(0, 4).map((item) => <button type="button" key={item.id} disabled={!item.url} onClick={() => onOpenMedia(item)} className="media-tile"><span>{item.type === 'document' ? <FileImage /> : <ScanSearch />}</span><small>{item.year || '未标注'}</small><em>{item.title}</em></button>)}
            {role !== 'guest' && <button type="button" className="media-tile add" onClick={onUpload}><span><Plus /></span><small>新增</small><em>上传资料</em></button>}
          </div>
        </section>

        <section className="details-section timeline-section">
          <div className="section-title"><h3><History />重要事件</h3></div>
          <div className="timeline-list">
            {personEvents.slice(0, 5).map((item) => <div key={item.id}><time>{item.year}</time><span><strong>{item.title}</strong><small>{item.description}</small></span></div>)}
          </div>
        </section>
      </div>

      <footer className="details-actions">
        <button type="button" className="button primary" onClick={onEdit}>{role === 'guest' ? '登录后编辑' : <><Edit3 />编辑资料</>}</button>
        <button type="button" className="button warm" onClick={onShare}><Share2 />分享谱系</button>
      </footer>
    </aside>
  );
}
